'use client'

import React, { useState } from 'react'
import { apiCall, apiKey, ApiError, useApi } from '@excellent/ui/api'
import { lens } from '@excellent/ui/api/contracts/lens'
import { Button, Card, PageHeader, Skeleton, SectionHeading } from "@excellent/ui"
import { formatDate } from '@lens/lib/decode'
import type {
  SkillInfo,
  PluginInfo,
  ExcellentInstallState,
} from '@lens/lib/claude-reader'

function formatBytes(b: number) {
 if (b >= 1_073_741_824) return (b / 1_073_741_824).toFixed(2) + ' GB'
 if (b >= 1_048_576) return (b / 1_048_576).toFixed(1) + ' MB'
 if (b >= 1_024) return (b / 1_024).toFixed(1) + ' KB'
 return b + ' B'
}

function JsonValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
 if (value === null) return <span className="text-foreground">null</span>
 if (typeof value === 'boolean')
 return <span className="text-foreground dark:text-[var(--chart-4)]">{String(value)}</span>
 if (typeof value === 'number')
 return <span className="text-foreground dark:text-[var(--chart-1)]">{value}</span>
 if (typeof value === 'string')
 return <span className="text-foreground dark:text-[var(--chart-4)]">&quot;{value}&quot;</span>
 if (Array.isArray(value)) {
 if (value.length === 0) return <span className="text-foreground">[]</span>
 return (
 <span>
 <span className="text-foreground">[</span>
 <div className="pl-4">
 {value.map((v, i) => (
 <div key={i}>
 <JsonValue value={v} depth={depth + 1} />
 {i < value.length - 1 && <span className="text-foreground">,</span>}
 </div>
 ))}
 </div>
 <span className="text-foreground">]</span>
 </span>
 )
 }
 if (typeof value === 'object') {
 const entries = Object.entries(value as Record<string, unknown>)
 if (entries.length === 0) return <span className="text-foreground">{'{}'}</span>
 return (
 <span>
 <span className="text-foreground">{'{'}</span>
 <div className="pl-4">
 {entries.map(([k, v], i) => (
 <div key={k}>
 <span className="text-foreground">&quot;</span>
 <span className="text-foreground dark:text-[var(--chart-2)]">{k}</span>
 <span className="text-foreground">&quot;</span>
 <span className="text-foreground">: </span>
 <JsonValue value={v} depth={depth + 1} />
 {i < entries.length - 1 && <span className="text-foreground">,</span>}
 </div>
 ))}
 </div>
 <span className="text-foreground">{'}'}</span>
 </span>
 )
 }
 return <span className="text-foreground">{String(value)}</span>
}

function Section({ title, children, anchor }: { title: string; children: React.ReactNode; anchor?: string }) {
 return (
 <Card id={anchor} className="gap-0 p-4 scroll-mt-24">
 <SectionHeading className="mb-4" title={title} />
 {children}
 </Card>
 )
}

function ExcellentSkillsCard({
 state,
 onChanged,
}: {
 state: ExcellentInstallState
 onChanged: () => void
}) {
 const [busy, setBusy] = useState<'install' | 'uninstall' | null>(null)
 const [output, setOutput] = useState<string | null>(null)
 const [error, setError] = useState<string | null>(null)

 const fullyInstalled =
 state.shipped.length > 0 &&
 state.installed.length === state.shipped.length &&
 state.mcp_registered

 async function run(action: 'install' | 'uninstall') {
 setBusy(action)
 setError(null)
 setOutput(null)
 try {
 const json = await apiCall(lens.excellentSkills, {
 body: { action },
 })
 if (!json.success) {
 setError(json.stderr || 'failed')
 }
 setOutput([json.stdout, json.stderr].filter(Boolean).join('\n').trim() || null)
 onChanged()
 } catch (err) {
 setError(err instanceof ApiError ? err.error : err instanceof Error ? err.message : String(err))
 } finally {
 setBusy(null)
 }
 }

 return (
 <Section title={`Excellent Skills (${state.installed.length}/${state.shipped.length})`}>
 {state.shipped.length === 0 ? (
 <p className="text-foreground type-body">
 No shipped skills found in packages/skills/. Build @excellent/mcp first.
 </p>
 ) : (
 <div className="space-y-3">
 <div className="flex items-center gap-3">
 <span
 className={`shrink-0 w-2 h-2 rounded-lg ${
 fullyInstalled ? 'bg-primary' : 'bg-muted-foreground'
 }`}
 />
 <span className="text-foreground type-body">
 {fullyInstalled
 ? 'All skills installed; MCP server registered.'
 : state.installed.length === 0
 ? 'Not installed. Click Install to symlink skills and register the MCP server.'
 : `${state.installed.length} of ${state.shipped.length} skills installed${
 state.mcp_registered ? '' : ' (MCP server not yet registered)'
 }.`}
 </span>
 </div>

 <div className="grid gap-1">
 {state.shipped.map((name) => {
 const installed = state.installed.includes(name)
 return (
 <div
 key={name}
 className="flex items-center gap-2 text-foreground type-body"
 >
 <span className="text-muted-foreground">
 {installed ? '✓' : '·'}
 </span>
 <span>{name}</span>
 {!installed && (
 <span className="text-muted-foreground">— not installed</span>
 )}
 </div>
 )
 })}
 </div>

 <div className="flex items-center gap-2 pt-1">
 {!fullyInstalled && (
 <Button
 type="button"
 size="sm"
 onClick={() => run('install')}
 disabled={busy !== null}
 >
 {busy === 'install' ? 'Installing…' : 'Install'}
 </Button>
 )}
 {(state.installed.length > 0 || state.mcp_registered) && (
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => run('uninstall')}
 disabled={busy !== null}
 >
 {busy === 'uninstall' ? 'Uninstalling…' : 'Uninstall'}
 </Button>
 )}
 {fullyInstalled && (
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => run('install')}
 disabled={busy !== null}
 >
 {busy === 'install' ? 'Reinstalling…' : 'Reinstall'}
 </Button>
 )}
 </div>

 {error && (
 <p className="text-destructive type-body">{error}</p>
 )}

 {output && (
 <pre className="surface-edge rounded p-2 text-muted-foreground type-body overflow-x-auto whitespace-pre-wrap">
 {output}
 </pre>
 )}
 </div>
 )}
 </Section>
 )
}

export default function SettingsPage() {
 const { data, error, isLoading, mutate } = useApi(lens.settings, {}) as {
 data: {
 settings: Record<string, unknown>
 storageBytes: number
 skills: SkillInfo[]
 plugins: PluginInfo[]
 excellent: ExcellentInstallState
 } | undefined
 error: unknown
 isLoading: boolean
 mutate: () => Promise<unknown>
 }

 return (
 <div className="flex flex-col">
 <div className="space-y-6">
 <PageHeader title="Settings" description="Your Claude Code setup — settings, skills, plugins, and storage." />
 {error ? <p className="text-destructive type-body ">Error: {String(error)}</p> : null}
 {isLoading && (
 <div className="space-y-4">
 {Array.from({ length: 3 }).map((_, i) => (
 <Skeleton key={i} className="h-32 rounded-lg" />
 ))}
 </div>
 )}
 {data && (
 <>
 <Section title="Storage" anchor="storage">
 <div className="flex items-center gap-3">
 <span className="text-foreground type-heading ">
 {formatBytes(data.storageBytes)}
 </span>
 <span className="text-foreground type-body ">used by ~/.claude/</span>
 </div>
 </Section>

 <Section title="Settings">
 {Object.keys(data.settings).length === 0 ? (
 <p className="text-foreground type-body ">No settings found in ~/.claude/settings.json</p>
 ) : (
 <div className=" type-body leading-body overflow-x-auto">
 <JsonValue value={data.settings} />
 </div>
 )}
 </Section>

 {data.settings.env && (
 <Section title="Environment Variables">
 <div className=" type-body leading-body overflow-x-auto">
 <JsonValue value={data.settings.env} />
 </div>
 </Section>
 )}

 {data.settings.mcpServers && (
 <Section title="MCP Servers" anchor="mcp">
 <div className="space-y-3">
 {Object.entries(data.settings.mcpServers as Record<string, unknown>).map(([name, cfg]) => (
 <div key={name} className="surface-edge rounded p-3">
 <p className="text-foreground type-body mb-2">{name}</p>
 <div className=" type-body text-foreground overflow-x-auto">
 <JsonValue value={cfg} />
 </div>
 </div>
 ))}
 </div>
 </Section>
 )}

 {data.excellent && (
 <ExcellentSkillsCard
 state={data.excellent}
 onChanged={() => { void mutate() }}
 />
 )}

 <Section title={`Skills (${data.skills.length})`} anchor="skills">
 {data.skills.length === 0 ? (
 <p className="text-foreground type-body ">No skills found in ~/.claude/skills/</p>
 ) : (
 <div className="grid gap-2">
 {data.skills.map(skill => (
 <div key={skill.name} className="surface-edge rounded p-3 flex items-start gap-3">
 <span className="shrink-0 w-2 h-2 mt-1.5 rounded-lg bg-primary" />
 <div className="min-w-0">
 <p className="text-foreground type-body ">{skill.name}</p>
 {skill.description && (
 <p className="text-foreground type-body mt-0.5">{skill.description}</p>
 )}
 {skill.triggers && (
 <p className="text-foreground type-body mt-1 leading-body line-clamp-2">{skill.triggers}</p>
 )}
 </div>
 </div>
 ))}
 </div>
 )}
 </Section>

 {data.plugins.length > 0 && (
 <Section title={`Plugins (${data.plugins.length})`} anchor="plugins">
 <div className="grid gap-2">
 {data.plugins.map((plugin, i) => (
 <div key={i} className="surface-edge rounded p-3 flex items-center justify-between gap-4">
 <div className="min-w-0">
 <p className="text-foreground type-body ">{plugin.id}</p>
 <p className="text-foreground type-body mt-0.5">scope: {plugin.scope}</p>
 </div>
 <div className="text-right shrink-0">
 <span className="text-foreground dark:text-[var(--chart-1)] type-body">v{plugin.version}</span>
 <p className="text-foreground type-body mt-0.5">
 {formatDate(plugin.installedAt)}
 </p>
 </div>
 </div>
 ))}
 </div>
 </Section>
 )}
 </>
 )}
 </div>
 </div>
 )
}
